import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
} from '@microsoft/signalr';
import { createContext, type ReactNode,useCallback, useContext, useEffect, useRef, useState } from 'react';

import { negotiateSignalR } from '../../../services/notification';

export type SignalRConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'failed';

export interface SignalRContextValue {
  status: SignalRConnectionStatus
  onNotification: (handler: () => void) => () => void
}

const SignalRContext = createContext<SignalRContextValue>({
  status: 'disconnected',
  onNotification: () => () => undefined,
})

export const useSignalR = () => useContext(SignalRContext)

// Event name emitted by the SignalR notifications hub
const NOTIFICATION_EVENT = 'NewAppNotificationCreated';

interface SignalRProviderProps {
  children: ReactNode
}

const SignalRProvider = ({ children }: SignalRProviderProps) => {
  const [status, setStatus] = useState<SignalRConnectionStatus>('disconnected')
  const listenersRef = useRef<Set<() => void>>(new Set())

  const onNotification = useCallback((handler: () => void) => {
    listenersRef.current.add(handler)
    return () => {
      listenersRef.current.delete(handler)
    }
  }, [])

  useEffect(() => {
    let connection: HubConnection | null = null
    let cancelled = false

    const start = async () => {
      try {
        const negotiateResult = await negotiateSignalR()
        if (cancelled) return
        connection = new HubConnectionBuilder()
          .withUrl(negotiateResult.url, {
            accessTokenFactory: () => negotiateResult.accessToken ?? '',
            withCredentials: false,
          })
          .withAutomaticReconnect()
          .build()

        connection.on(NOTIFICATION_EVENT, (_message) => {
          listenersRef.current.forEach(listener => listener())
        })

        connection.onreconnecting(() => setStatus('reconnecting'))
        connection.onreconnected(() => setStatus('connected'))
        connection.onclose(() => setStatus('disconnected'))

        setStatus('connecting')
        await connection.start()

        if (cancelled) {
          await connection.stop()
          return
        }

        setStatus('connected')
      } catch (error) {
        if (!cancelled) setStatus('failed')
      }
    }

    start()

    return () => {
      cancelled = true
      if (connection && connection.state !== HubConnectionState.Disconnected) {
        connection.stop()
      }
    }
  }, [])

  return (
    <SignalRContext.Provider value={{ status, onNotification }}>
      {children}
    </SignalRContext.Provider>
  );
}

export default SignalRProvider;
